import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Wallet, Building2, History, Loader2, Plus, Star, Trash2,
  CheckCircle, Clock, ExternalLink,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { usePayments } from '@/hooks/use-payments';
import { useStripeOnboarding } from '@/hooks/use-stripe-onboarding';
import { useCurrentFilmmakerProfile } from '@/hooks/use-filmmaker-banking';

// ── Dialog ────────────────────────────────────────────────────────────────────

interface FilmmakerWalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddBank: (data: {
    accountName: string;
    sortCode: string;
    accountNumber: string;
    isDefault: boolean;
  }) => Promise<unknown>;
  isCreatingOnboardingLink: boolean;
}

export const FilmmakerWalletDialog: React.FC<FilmmakerWalletDialogProps> = ({
  open,
  onOpenChange,
  onAddBank,
  isCreatingOnboardingLink,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'accounts' | 'earnings'>('accounts');
  const [isAddingBank, setIsAddingBank] = useState(false);
  const [formData, setFormData] = useState({
    accountName: '',
    sortCode: '',
    accountNumber: '',
  });

  const {
    paymentMethods,
    paymentHistory,
    totalEarnings,
    isLoading,
    setDefaultPaymentMethod,
    deletePaymentMethod,
  } = usePayments();

  const handleAddBank = async () => {
    if (!formData.accountName || !formData.sortCode || !formData.accountNumber) {
      toast.error('Please fill in all fields');
      return;
    }
    const cleanSortCode = formData.sortCode.replace(/[-\s]/g, '');
    if (cleanSortCode.length !== 6) {
      toast.error('Sort code must be 6 digits');
      return;
    }
    if (formData.accountNumber.replace(/\s/g, '').length !== 8) {
      toast.error('Account number must be 8 digits');
      return;
    }
    try {
      await onAddBank({
        accountName: formData.accountName,
        sortCode: cleanSortCode,
        accountNumber: formData.accountNumber,
        isDefault: paymentMethods.length === 0,
      });
      setIsAddingBank(false);
      setFormData({ accountName: '', sortCode: '', accountNumber: '' });
      onOpenChange(false);
    } catch {
      // error handled in hook
    }
  };

  const recentPayments = paymentHistory.slice(0, 15);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            Earnings & Payments
          </DialogTitle>
          <DialogDescription>
            Total Earnings:{' '}
            <span className="font-semibold text-foreground">{totalEarnings}</span>
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'accounts' | 'earnings')}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="accounts" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Bank Accounts
            </TabsTrigger>
            <TabsTrigger value="earnings" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              History
            </TabsTrigger>
          </TabsList>

          {/* ── Bank Accounts ── */}
          <TabsContent value="accounts" className="mt-4 space-y-3">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(2)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full rounded-lg" />
                ))}
              </div>
            ) : paymentMethods.length === 0 && !isAddingBank ? (
              <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                <Building2 className="h-8 w-8 mb-2" />
                <p className="text-sm">No bank accounts added</p>
                <p className="text-xs mt-1">Add a bank account to receive payments</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-auto pr-1">
                {paymentMethods.map((account) => (
                  <div
                    key={account.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium text-sm">{account.accountName}</span>
                        {account.isDefault && (
                          <Badge variant="secondary" className="text-[10px] h-4 px-1.5 rounded-sm">
                            Default
                          </Badge>
                        )}
                        {account.stripe_onboarding_completed ? (
                          <Badge className="text-[10px] h-4 px-1.5 rounded-sm bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-0">
                            <CheckCircle className="h-2.5 w-2.5 mr-0.5" /> Verified
                          </Badge>
                        ) : account.stripe_account_id ? (
                          <Badge className="text-[10px] h-4 px-1.5 rounded-sm bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-0">
                            <Clock className="h-2.5 w-2.5 mr-0.5" /> Pending
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        ••••{account.accountNumber.slice(-4)} &middot; {account.sortCode}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      {!account.isDefault && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Set as default"
                          onClick={() => setDefaultPaymentMethod.mutate(account.id)}
                          disabled={setDefaultPaymentMethod.isPending}
                        >
                          <Star className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        title="Remove account"
                        onClick={() => {
                          if (
                            confirm(
                              `Remove "${account.accountName}" (••••${account.accountNumber.slice(-4)})?`
                            )
                          ) {
                            deletePaymentMethod.mutate(account.id);
                          }
                        }}
                        disabled={deletePaymentMethod.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isAddingBank ? (
              <div className="space-y-3 pt-3 border-t">
                <p className="text-xs text-muted-foreground">
                  You'll be redirected to Stripe to verify your account after adding.
                </p>
                <div>
                  <Label htmlFor="fm-acct-name" className="text-xs font-medium">
                    Account Name
                  </Label>
                  <Input
                    id="fm-acct-name"
                    className="mt-1 h-9"
                    placeholder="e.g. John Smith"
                    value={formData.accountName}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, accountName: e.target.value }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="fm-sort-code" className="text-xs font-medium">
                      Sort Code
                    </Label>
                    <Input
                      id="fm-sort-code"
                      className="mt-1 h-9"
                      placeholder="12-34-56"
                      value={formData.sortCode}
                      onChange={(e) =>
                        setFormData((f) => ({ ...f, sortCode: e.target.value }))
                      }
                      maxLength={8}
                    />
                  </div>
                  <div>
                    <Label htmlFor="fm-acct-num" className="text-xs font-medium">
                      Account Number
                    </Label>
                    <Input
                      id="fm-acct-num"
                      className="mt-1 h-9"
                      placeholder="12345678"
                      value={formData.accountNumber}
                      onChange={(e) =>
                        setFormData((f) => ({ ...f, accountNumber: e.target.value }))
                      }
                      maxLength={8}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingBank(false);
                      setFormData({ accountName: '', sortCode: '', accountNumber: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleAddBank} disabled={isCreatingOnboardingLink}>
                    {isCreatingOnboardingLink && (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    )}
                    Add & Verify with Stripe
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1.5"
                onClick={() => setIsAddingBank(true)}
              >
                <Plus className="h-4 w-4" />
                Add Bank Account
              </Button>
            )}
          </TabsContent>

          {/* ── Earnings History ── */}
          <TabsContent value="earnings" className="mt-4">
            <div className="max-h-[300px] overflow-auto rounded-md border">
              {isLoading ? (
                <div className="p-3 space-y-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 flex-1" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  ))}
                </div>
              ) : recentPayments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mb-2" />
                  <p className="text-sm">No payments yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader className="bg-muted/50 sticky top-0">
                    <TableRow>
                      <TableHead className="w-[80px]">Date</TableHead>
                      <TableHead>Job</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentPayments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="text-xs text-muted-foreground">
                          {payment.paymentDate
                            ? format(new Date(payment.paymentDate), 'MMM d')
                            : '—'}
                        </TableCell>
                        <TableCell
                          className="text-xs max-w-[140px] truncate"
                          title={payment.title}
                        >
                          {payment.title}
                        </TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium',
                              payment.status === 'Completed'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            )}
                          >
                            {payment.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium text-sm">
                          £{(payment.amount || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  onOpenChange(false);
                  navigate('/filmmaker/dashboard/opportunities?filter=earnings');
                }}
              >
                View full earnings
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// ── Self-contained button for GlobalHeader ────────────────────────────────────

export const FilmmakerWalletButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: filmmakerProfile } = useCurrentFilmmakerProfile();
  const { totalEarnings, isLoading } = usePayments();
  const { handleAddPaymentMethodWithStripe, isCreatingOnboardingLink } = useStripeOnboarding();

  // Only render for filmmakers
  if (!filmmakerProfile) return null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-muted transition-colors group text-sm font-medium text-muted-foreground hover:text-foreground"
        title="Earnings & Bank Accounts"
      >
        <Wallet className="h-4 w-4" />
        <span>{isLoading ? '...' : totalEarnings}</span>
      </button>
      <FilmmakerWalletDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        onAddBank={handleAddPaymentMethodWithStripe}
        isCreatingOnboardingLink={isCreatingOnboardingLink}
      />
    </>
  );
};

export default FilmmakerWalletDialog;
